import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'

interface MapSelectorProps {
  latitude: string
  longitude: string
  onLocationChange: (lat: string, lng: string) => void
  height?: string
  className?: string
}

export default function MapSelector({
  latitude,
  longitude,
  onLocationChange,
  height = '300px',
  className = ''
}: MapSelectorProps) {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<maplibregl.Map | null>(null)
  const marker = useRef<maplibregl.Marker | null>(null)

  // Default to Warsaw if no coordinates provided
  const defaultCenter: [number, number] = [21.0122, 52.2297] // [lng, lat] for MapLibre

  // Parse coordinates or use defaults
  const lat = parseFloat(latitude) || defaultCenter[1]
  const lng = parseFloat(longitude) || defaultCenter[0]
  const center: [number, number] = [lng, lat]

  useEffect(() => {
    if (map.current) return // initialize map only once

    map.current = new maplibregl.Map({
      container: mapContainer.current!,
      style: {
        version: 8,
        sources: {
          'osm': {
            type: 'raster',
            tiles: [
              'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
            ],
            tileSize: 256,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }
        },
        layers: [
          {
            id: 'osm-layer',
            type: 'raster',
            source: 'osm',
            minzoom: 0,
            maxzoom: 20
          }
        ]
      },
      center: center,
      zoom: 13,
      maxZoom: 20,
      minZoom: 1
    })

    // Add marker if coordinates are provided
    if (latitude && longitude) {
      marker.current = new maplibregl.Marker({ color: '#3B82F6', draggable: true })
        .setLngLat([lng, lat])
        .setPopup(new maplibregl.Popup().setHTML(`
          <div class="text-center">
            <div class="font-medium">Aktualna lokalizacja</div>
            <div class="text-sm text-gray-600">
              ${lat.toFixed(6)}, ${lng.toFixed(6)}
            </div>
            <div class="text-xs text-gray-500 mt-1">
              Przeciągnij aby zmienić
            </div>
          </div>
        `))
        .addTo(map.current!)

      // Handle marker drag
      marker.current.on('dragend', () => {
        const lngLat = marker.current!.getLngLat()
        onLocationChange(lngLat.lat.toFixed(6), lngLat.lng.toFixed(6))
      })
    }

    // Handle map clicks
    map.current.on('click', (e) => {
      const { lng, lat } = e.lngLat

      // Remove existing marker
      if (marker.current) {
        marker.current.remove()
      }

      // Add new marker
      marker.current = new maplibregl.Marker({ color: '#3B82F6', draggable: true })
        .setLngLat([lng, lat])
        .setPopup(new maplibregl.Popup().setHTML(`
          <div class="text-center">
            <div class="font-medium">Wybrana lokalizacja</div>
            <div class="text-sm text-gray-600">
              ${lat.toFixed(6)}, ${lng.toFixed(6)}
            </div>
            <div class="text-xs text-gray-500 mt-1">
              Przeciągnij aby zmienić
            </div>
          </div>
        `))
        .addTo(map.current!)

      // Handle marker drag
      marker.current.on('dragend', () => {
        const lngLat = marker.current!.getLngLat()
        onLocationChange(lngLat.lat.toFixed(6), lngLat.lng.toFixed(6))
      })

      onLocationChange(lat.toFixed(6), lng.toFixed(6))
    })

    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [])

  // Update marker position when coordinates change
  useEffect(() => {
    if (map.current && latitude && longitude) {
      const newLng = parseFloat(longitude)
      const newLat = parseFloat(latitude)

      if (marker.current) {
        marker.current.setLngLat([newLng, newLat])
      } else {
        marker.current = new maplibregl.Marker({ color: '#3B82F6', draggable: true })
          .setLngLat([newLng, newLat])
          .setPopup(new maplibregl.Popup().setHTML(`
            <div class="text-center">
              <div class="font-medium">Aktualna lokalizacja</div>
              <div class="text-sm text-gray-600">
                ${newLat.toFixed(6)}, ${newLng.toFixed(6)}
              </div>
              <div class="text-xs text-gray-500 mt-1">
                Przeciągnij aby zmienić
              </div>
            </div>
          `))
          .addTo(map.current)

        if (marker.current) {
          marker.current.on('dragend', () => {
            const lngLat = marker.current!.getLngLat()
            onLocationChange(lngLat.lat.toFixed(6), lngLat.lng.toFixed(6))
          })
        }
      }
    }
  }, [latitude, longitude, onLocationChange])

  return (
    <div className={`relative rounded-lg overflow-hidden border border-gray-300 ${className}`}>
      <div
        ref={mapContainer}
        style={{ height, width: '100%' }}
        className="z-0"
      />

      <div className="absolute top-2 left-2 bg-white bg-opacity-90 p-2 rounded shadow-sm text-sm">
        <div className="text-gray-700 font-medium">Wybierz lokalizację</div>
        <div className="text-gray-600 text-xs mt-1">
          Kliknij na mapie lub przeciągnij znacznik
        </div>
      </div>

      {latitude && longitude && (
        <div className="absolute bottom-2 right-2 bg-white bg-opacity-90 p-2 rounded shadow-sm text-sm">
          <div className="text-gray-700">
            📍 {lat.toFixed(6)}, {lng.toFixed(6)}
          </div>
        </div>
      )}
    </div>
  )
}
